import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {
  AiAdminOwner,
  AiChatConfig,
  AiConversationDetail,
  AiConversationSummary,
} from '../../../types';
import useAiApiRoutes from '../../hooks/api/useAiApiRoutes';
import UserContext from '../../context/user';
import AiConversationViewer from '../../components/ai/AiConversationViewer';
import AiEmailDecisionsPanel from '../../components/ai/AiEmailDecisionsPanel';

const BRAND = '#1eaaf1';
const formatTime = (value: string | null) => (value ? new Date(value).toLocaleString() : '');

const EmptyPane = ({ text }: { text: string }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="100%"
    gap={1}
    color="text.disabled"
  >
    <ForumOutlinedIcon sx={{ fontSize: 44 }} />
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

// --- Conversations browser (all users' conversations, with query disclosures)

const ConversationBrowser = ({ owners }: { owners: { id: string; name: string }[] }) => {
  const { getAdminConversations, getAdminConversation } = useAiApiRoutes();
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [conversations, setConversations] = useState<AiConversationSummary[] | null>(null);
  const [selected, setSelected] = useState<AiConversationDetail | null>(null);

  const load = useCallback(() => {
    getAdminConversations(ownerFilter || undefined, page).then((result) => {
      if (result) {
        setConversations(result.conversations);
        setTotalPages(result.totalPages);
        setTotalItems(result.totalItems);
      }
    });
  }, [getAdminConversations, ownerFilter, page]);

  useEffect(load, [load]);

  return (
    <Paper
      variant="outlined"
      sx={{ display: 'flex', flex: 1, minHeight: 0, borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        width={380}
        minWidth={320}
        display="flex"
        flexDirection="column"
        sx={{ borderRight: '1px solid', borderColor: 'divider' }}
      >
        <Box px={2} py={1.5} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControl fullWidth size="small">
            <InputLabel id="ai-owner-filter">Account</InputLabel>
            <Select
              labelId="ai-owner-filter"
              label="Account"
              value={ownerFilter}
              onChange={(e) => {
                setPage(1);
                setSelected(null);
                setOwnerFilter(e.target.value);
              }}
            >
              <MenuItem value="">All accounts</MenuItem>
              <MenuItem value="cross">Cross-account (admin) sessions</MenuItem>
              <Divider />
              {owners.map((owner) => (
                <MenuItem key={owner.id} value={owner.id}>
                  {owner.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
            {conversations === null ? 'Loading…' : `${totalItems} conversation${totalItems === 1 ? '' : 's'}`}
          </Typography>
        </Box>

        <Box flex={1} minHeight={0} sx={{ overflowY: 'auto' }}>
          {conversations === null ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress size={20} />
            </Box>
          ) : conversations.length === 0 ? (
            <EmptyPane text="No conversations for this filter yet." />
          ) : (
            <List dense disablePadding>
              {conversations.map((conversation, index) => (
                <Box key={conversation.id}>
                  {index > 0 ? <Divider component="li" /> : null}
                  <ListItemButton
                    selected={selected?.id === conversation.id}
                    onClick={() => getAdminConversation(conversation.id).then((d) => d && setSelected(d))}
                    sx={{
                      py: 1.25,
                      '&.Mui-selected': { borderLeft: `3px solid ${BRAND}`, pl: '13px' },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: conversation.crossClient ? 'rgba(37,99,235,0.12)' : 'rgba(30,170,241,0.12)',
                        }}
                      >
                        {conversation.crossClient ? (
                          <PublicIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                        ) : (
                          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: BRAND }} />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={conversation.preview || '(no messages)'}
                      primaryTypographyProps={{ noWrap: true, fontSize: 14 }}
                      secondary={`${conversation.ownerName ?? ''}${
                        conversation.userName ? ` · ${conversation.userName}` : ''
                      } · ${conversation.messageCount} msgs · ${formatTime(conversation.lastMessageAt)}`}
                      secondaryTypographyProps={{ noWrap: true, fontSize: 12 }}
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
          )}
        </Box>

        {totalPages > 1 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            py={0.75}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          >
            <IconButton size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Page {page} of {totalPages}
            </Typography>
            <IconButton
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
      </Box>

      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        {selected ? (
          <>
            <Box px={2.5} py={1.5} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2">
                {selected.ownerName || 'Unknown account'}
                {selected.userName ? ` — ${selected.userName}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selected.userEmail ? `${selected.userEmail} · ` : ''}
                {formatTime(selected.lastMessageAt)}
              </Typography>
            </Box>
            <Box flex={1} minHeight={0} sx={{ overflowY: 'auto', px: 2.5, py: 2, background: '#fafbfd' }}>
              <AiConversationViewer conversation={selected} showQueries hideHeader />
            </Box>
          </>
        ) : (
          <EmptyPane text="Select a conversation to review it." />
        )}
      </Box>
    </Paper>
  );
};

// --- Access panel (per-owner StackIQ toggles)

const AccessPanel = () => {
  const { getAdminOwners, toggleOwnerAi } = useAiApiRoutes();
  const [owners, setOwners] = useState<AiAdminOwner[] | null>(null);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    getAdminOwners().then((result) => setOwners(result?.owners ?? []));
  }, [getAdminOwners]);

  const filtered = useMemo(() => {
    if (!owners) return [];
    const query = search.trim().toLowerCase();
    if (!query) return owners;
    return owners.filter(
      (owner) =>
        owner.name.toLowerCase().includes(query) || (owner.email || '').toLowerCase().includes(query)
    );
  }, [owners, search]);

  const toggle = async (owner: AiAdminOwner) => {
    setSavingId(owner.id);
    const result = await toggleOwnerAi(owner.id, !owner.aiEnabled);
    if (result && owners) {
      setOwners(owners.map((o) => (o.id === result.id ? { ...o, aiEnabled: result.aiEnabled } : o)));
    }
    setSavingId(null);
  };

  const enabledCount = owners ? owners.filter((o) => o.aiEnabled).length : 0;

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, maxWidth: 760, width: '100%', overflow: 'hidden', mx: 'auto', borderColor: '#e6ebf1' }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box>
          <Typography variant="subtitle2">Client access</Typography>
          <Typography variant="caption" color="text.secondary">
            Choose which accounts can use StackIQ
          </Typography>
        </Box>
        {owners ? (
          <Chip
            size="small"
            label={`${enabledCount} of ${owners.length} enabled`}
            sx={{ background: 'rgba(30,170,241,0.12)', color: '#0f7ab5', fontWeight: 600 }}
          />
        ) : null}
      </Box>
      <Box px={2} py={1.5}>
        <TextField
          size="small"
          placeholder="Search accounts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {owners === null ? (
        <Box textAlign="center" py={4}>
          <CircularProgress size={20} />
        </Box>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary" variant="body2" px={2} pb={2}>
          No accounts match “{search}”.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 480, overflowY: 'auto' }}>
          {filtered.map((owner, index) => (
            <Box key={owner.id}>
              {index > 0 ? <Divider component="li" /> : null}
              <ListItemButton onClick={() => toggle(owner)} disabled={savingId === owner.id} sx={{ py: 1 }}>
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: owner.aiEnabled ? 'rgba(30,170,241,0.15)' : 'grey.100', color: owner.aiEnabled ? '#0f7ab5' : 'text.disabled', fontSize: 14 }}>
                    {(owner.name || '?').slice(0, 1).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={owner.name}
                  secondary={owner.email}
                  primaryTypographyProps={{ fontSize: 14 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
                <Switch
                  edge="end"
                  checked={owner.aiEnabled}
                  disabled={savingId === owner.id}
                  inputProps={{ 'aria-label': `StackIQ access for ${owner.name}` }}
                />
              </ListItemButton>
            </Box>
          ))}
        </List>
      )}
    </Paper>
  );
};

// --- KPI stat card for the page header ---------------------------------

type StatDef = {
  key: 'emails' | 'review' | 'conversations' | 'access';
  label: string;
  value: number | null;
  caption: string;
  icon: ReactNode;
  attention?: boolean;
  targetView: 'emails' | 'conversations' | 'access';
  // Email-decision status filter to apply when the card is clicked.
  targetStatus?: string;
};

const StatCard = ({ stat, onClick }: { stat: StatDef; onClick: () => void }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    role="button"
    aria-label={`${stat.label} — open`}
    sx={{
      flex: '1 1 180px',
      minWidth: 170,
      borderRadius: 3,
      px: 2.25,
      py: 1.75,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      borderColor: stat.attention ? '#fdba74' : '#e6ebf1',
      background: stat.attention ? '#fff7ed' : '#fff',
      transition: 'box-shadow 150ms ease, transform 150ms ease',
      '&:hover': { boxShadow: '0 4px 14px rgba(16,42,67,0.08)', transform: 'translateY(-1px)' },
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: stat.attention ? '#c2410c' : '#0f7ab5',
        background: stat.attention ? 'rgba(249,115,22,0.12)' : 'rgba(0,136,207,0.10)',
        flexShrink: 0,
      }}
    >
      {stat.icon}
    </Box>
    <Box minWidth={0}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {stat.value === null ? '—' : stat.value}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {stat.label}
      </Typography>
    </Box>
  </Paper>
);

// --- Page: email decisions + conversation oversight + access panel.
// Chatting itself lives in the StackIQ side panel, available on every page.

const AssistantAdmin = () => {
  const { getChatConfig, getAdminConversations, getAdminOwners, getEmailDecisions } = useAiApiRoutes();
  const { constants } = useContext(UserContext);
  // Conversations and Client Access exist only to oversee the chat companion,
  // so they go with it when it is switched off. Email decisions stay.
  const chatEnabled = constants.aiChatEnabled === true;
  const [view, setView] = useState<'emails' | 'conversations' | 'access'>('emails');
  // Status filter for the email decisions list — the stat cards set it.
  const [decisionStatus, setDecisionStatus] = useState('');
  // ?decision=<id> comes from the admin notice email — open that record.
  const location = useLocation();
  const history = useHistory();
  const focusRecordId = new URLSearchParams(location.search).get('decision') || undefined;
  const clearFocus = () => history.replace(location.pathname);
  useEffect(() => {
    if (focusRecordId) setView('emails');
  }, [focusRecordId]);
  const [owners, setOwners] = useState<{ id: string; name: string }[]>([]);
  const [stats, setStats] = useState<{
    emails: number | null;
    review: number | null;
    conversations: number | null;
    enabled: number | null;
    total: number | null;
  }>({ emails: null, review: null, conversations: null, enabled: null, total: null });

  useEffect(() => {
    // Those endpoints are switched off with the companion, so don't call them.
    if (!chatEnabled) return;
    getChatConfig().then((config: AiChatConfig | undefined) => {
      if (config && config.admin) setOwners(config.owners);
    });
  }, [chatEnabled, getChatConfig]);

  const loadStats = useCallback(() => {
    getEmailDecisions(1).then((r) => setStats((s) => ({ ...s, emails: r?.totalItems ?? null })));
    getEmailDecisions(1, 'needs_review').then((r) => setStats((s) => ({ ...s, review: r?.totalItems ?? null })));
    if (!chatEnabled) return;
    getAdminConversations(undefined, 1).then((r) => setStats((s) => ({ ...s, conversations: r?.totalItems ?? null })));
    getAdminOwners().then((r) =>
      setStats((s) => ({ ...s, enabled: r?.enabledCount ?? null, total: r?.totalCount ?? null }))
    );
  }, [chatEnabled, getEmailDecisions, getAdminConversations, getAdminOwners]);

  useEffect(loadStats, [loadStats]);

  // Conversation and access counts belong to the companion, so they go
  // when it does, leaving the email pipeline stats.
  const statDefs: StatDef[] = (
    [
    {
      key: 'emails',
      label: 'Emails processed',
      caption: '',
      value: stats.emails,
      icon: <MarkEmailReadOutlinedIcon fontSize="small" />,
      targetView: 'emails',
      targetStatus: '',
    },
    {
      key: 'review',
      label: 'Awaiting review',
      caption: '',
      value: stats.review,
      icon: <RuleOutlinedIcon fontSize="small" />,
      attention: (stats.review ?? 0) > 0,
      targetView: 'emails',
      targetStatus: 'needs_review',
    },
    {
      key: 'conversations',
      label: 'Conversations',
      caption: '',
      value: stats.conversations,
      icon: <ForumOutlinedIcon fontSize="small" />,
      targetView: 'conversations',
    },
    {
      key: 'access',
      label:
        stats.total !== null ? `Clients enabled (of ${stats.total})` : 'Clients enabled',
      caption: '',
      value: stats.enabled,
      icon: <GroupOutlinedIcon fontSize="small" />,
      targetView: 'access',
    },
  ] as StatDef[]
  ).filter((stat) => chatEnabled || (stat.key !== 'conversations' && stat.key !== 'access'));

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 2, md: 4 }, pt: 3, pb: 4 }}>
        {/* Page header */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #0088CF, #00B7B2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,136,207,0.30)',
            }}
          >
            <AutoAwesomeIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              StackIQ Admin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI email decisions, conversation oversight, and client access
            </Typography>
          </Box>
        </Box>

        {/* KPI row */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          {statDefs.map((stat) => (
            <StatCard
              key={stat.key}
              stat={stat}
              onClick={() => {
                setView(stat.targetView);
                if (stat.targetStatus !== undefined) setDecisionStatus(stat.targetStatus);
              }}
            />
          ))}
        </Box>

        {/* Segmented tabs */}
        <Tabs
          value={view}
          onChange={(_, next) => setView(next)}
          sx={{
            minHeight: 42,
            mb: 2.5,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2.25,
              color: 'text.secondary',
              transition: 'background 150ms ease',
              '&:hover': { background: 'rgba(0,136,207,0.06)' },
            },
            '& .Mui-selected': { color: `${BRAND} !important`, background: 'rgba(0,136,207,0.10)' },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab label="Email Decisions" value="emails" />
          {chatEnabled ? <Tab label="Conversations" value="conversations" /> : null}
          {chatEnabled ? <Tab label="Client Access" value="access" /> : null}
        </Tabs>

        {view === 'emails' ? (
          <AiEmailDecisionsPanel
            onChanged={loadStats}
            statusFilter={decisionStatus}
            onStatusFilterChange={setDecisionStatus}
            focusRecordId={focusRecordId}
            onClearFocus={clearFocus}
          />
        ) : view === 'conversations' ? (
          <Box sx={{ height: 'calc(100vh - 420px)', minHeight: 420, display: 'flex' }}>
            <ConversationBrowser owners={owners} />
          </Box>
        ) : (
          <AccessPanel />
        )}
      </Box>
    </Box>
  );
};

export default AssistantAdmin;
